import { useEffect, useState } from "react";

import { Button, Modal } from "./ui";

type BeforeInstallPromptChoice = {
  outcome: "accepted" | "dismissed";
  platform: string;
};

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<BeforeInstallPromptChoice>;
  prompt: () => Promise<void>;
}

type NavigatorWithStandalone = Navigator & {
  standalone?: boolean;
};

const DISMISSED_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 30;
const DISMISS_MS = DISMISS_DAYS * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 3000;

export function PwaInstallPrompt(): JSX.Element | null {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [ready, setReady] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isDismissedRecently() || isStandalone()) return undefined;
    const timer = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isDismissedRecently() || isStandalone()) return undefined;
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setShowIosGuide(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (!ready || installEvent !== null || isDismissedRecently() || isStandalone()) return;
    setShowIosGuide(isIosSafari());
  }, [ready, installEvent]);

  if (!ready || (installEvent === null && !showIosGuide)) return null;
  if (showIosGuide) {
    return <IosInstallGuide onDismiss={() => dismiss(setInstallEvent, setShowIosGuide)} />;
  }
  if (installEvent === null) return null;
  return (
    <AndroidInstallPrompt
      onInstall={() => void promptInstall(installEvent, setInstallEvent)}
      onDismiss={() => dismiss(setInstallEvent, setShowIosGuide)}
    />
  );
}

function AndroidInstallPrompt({
  onInstall,
  onDismiss,
}: {
  onInstall: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="pwa-install-card fixed inset-x-4 bottom-4 z-50 rounded-xl bg-slate-900 p-4 text-white shadow-2xl shadow-slate-950/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-base font-medium leading-relaxed text-slate-50">
          ホーム画面に追加すると便利です
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" onClick={onInstall}>
            インストール
          </Button>
          <Button
            variant="ghost"
            onClick={onDismiss}
            className="text-slate-100 hover:bg-slate-800"
          >
            あとで
          </Button>
        </div>
      </div>
    </div>
  );
}

function IosInstallGuide({ onDismiss }: { onDismiss: () => void }): JSX.Element {
  return (
    <Modal open title="📱 ホーム画面に追加する" onClose={onDismiss}>
      <div className="space-y-4">
        <ol className="space-y-3 text-sm leading-6 text-text-secondary">
          <li className="rounded-md bg-bg p-3">1. 画面下部の [共有] ボタン (□↑) をタップ</li>
          <li className="rounded-md bg-bg p-3">2. 「ホーム画面に追加」をタップ</li>
          <li className="rounded-md bg-bg p-3">3. 「追加」をタップ</li>
        </ol>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onDismiss}>
            閉じる
          </Button>
        </div>
      </div>
    </Modal>
  );
}

async function promptInstall(
  event: BeforeInstallPromptEvent,
  setInstallEvent: (event: BeforeInstallPromptEvent | null) => void,
): Promise<void> {
  try {
    await event.prompt();
    await event.userChoice;
  } finally {
    setInstallEvent(null);
    saveDismissedAt();
  }
}

function dismiss(
  setInstallEvent: (event: BeforeInstallPromptEvent | null) => void,
  setShowIosGuide: (show: boolean) => void,
): void {
  saveDismissedAt();
  setInstallEvent(null);
  setShowIosGuide(false);
}

function isDismissedRecently(): boolean {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_KEY) ?? "0");
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_MS;
  } catch {
    return false;
  }
}

function saveDismissedAt(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    return;
  }
}

function isStandalone(): boolean {
  const navigatorWithStandalone = window.navigator as NavigatorWithStandalone;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigatorWithStandalone && navigatorWithStandalone.standalone === true)
  );
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}
